// Global toast store + renderer. Deliberately framework-light: a module-level
// store with useSyncExternalStore so non-React callers (e.g. the React Query
// MutationCache in main.tsx) can push toasts without a context/provider.
//
// Primary use: surface mutation failures app-wide so no form submit can fail
// silently. Individual forms may still render their own inline error (e.g.
// admin-branding's save.isError), the toast is the safety net.
import { useSyncExternalStore } from "react";

export type ToastKind = "error" | "success";
export interface Toast {
  id: number;
  message: string;
  kind: ToastKind;
}

let toasts: Toast[] = [];
const listeners = new Set<() => void>();
let nextId = 1;

function emit() {
  for (const l of listeners) l();
}

export function pushToast(message: string, kind: ToastKind = "error") {
  const id = nextId++;
  toasts = [...toasts, { id, message, kind }];
  emit();
  // Auto-dismiss; errors linger a touch longer than successes.
  setTimeout(() => dismissToast(id), kind === "error" ? 7000 : 4000);
  return id;
}

export function dismissToast(id: number) {
  toasts = toasts.filter((t) => t.id !== id);
  emit();
}

function subscribe(cb: () => void) {
  listeners.add(cb);
  return () => {
    listeners.delete(cb);
  };
}

function getSnapshot() {
  return toasts;
}

export function Toaster() {
  const items = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
  if (!items.length) return null;
  return (
    <div className="toast-stack" aria-live="assertive" role="region">
      {items.map((t) => (
        <div
          key={t.id}
          className={`toast toast--${t.kind}`}
          role={t.kind === "error" ? "alert" : "status"}
        >
          <span className="toast-msg">{t.message}</span>
          <button
            type="button"
            className="toast-close"
            aria-label="Dismiss"
            onClick={() => dismissToast(t.id)}
          >
            ×
          </button>
        </div>
      ))}
    </div>
  );
}
